import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, Calculator, TrendingUp, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import QuickNav from '@/components/QuickNav';

import { API } from '@/lib/api';

const SimulatorPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [investments, setInvestments] = useState([]);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    initial_amount: '',
    monthly_contribution: '',
    period_months: '12',
    investment_type: 'tesouro_selic'
  });

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API}/investments/types`);
      setInvestments(response.data.types);
    } catch (error) {
      console.error('Error fetching investments:', error);
    }
  };

  const handleSimulate = async () => {
    if (!formData.initial_amount || parseFloat(formData.initial_amount) < 0) {
      toast.error('Informe o valor inicial');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/investments/simulate`, {
        initial_amount: parseFloat(formData.initial_amount) || 0,
        monthly_contribution: parseFloat(formData.monthly_contribution) || 0,
        period_months: parseInt(formData.period_months),
        investment_type: formData.investment_type
      });
      setResult(response.data);
    } catch (error) {
      console.error('Simulation error:', error);
      toast.error('Erro ao simular investimento');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const periodOptions = [
    { value: '6', label: '6 meses' },
    { value: '12', label: '1 ano' },
    { value: '24', label: '2 anos' },
    { value: '36', label: '3 anos' },
    { value: '60', label: '5 anos' },
    { value: '120', label: '10 anos' }
  ];

  const investmentOptions = [
    { value: 'poupanca', label: 'Poupança' },
    { value: 'cdb', label: 'CDB (100% CDI)' },
    { value: 'tesouro_selic', label: 'Tesouro Selic' },
    { value: 'tesouro_ipca', label: 'Tesouro IPCA+' },
    { value: 'lci_lca', label: 'LCI/LCA' }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white ml-12 sm:ml-14"
              onClick={() => navigate('/investments')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Investimentos
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="font-bold text-white">Evite Dívidas</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] text-sm font-medium mb-6">
              <Calculator className="w-4 h-4" />
              Simulador de Investimentos
            </span>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Calcule seus Rendimentos
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Veja quanto seu dinheiro pode render em diferentes tipos de investimento
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Form */}
            <motion.div
              className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-xl font-bold text-white mb-6">Configure sua simulação</h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-white mb-2 block">Tipo de Investimento</Label>
                  <Select 
                    value={formData.investment_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, investment_type: value }))}
                  >
                    <SelectTrigger className="bg-[#121212] border-white/10 text-white" data-testid="investment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      {investmentOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-white/10">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Valor Inicial</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                    <Input
                      type="number"
                      value={formData.initial_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, initial_amount: e.target.value }))}
                      placeholder="0,00"
                      className="bg-[#121212] border-white/10 text-white pl-12"
                      data-testid="initial-amount-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Aporte Mensal</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                    <Input
                      type="number"
                      value={formData.monthly_contribution}
                      onChange={(e) => setFormData(prev => ({ ...prev, monthly_contribution: e.target.value }))}
                      placeholder="0,00"
                      className="bg-[#121212] border-white/10 text-white pl-12"
                      data-testid="monthly-contribution-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Periodo</Label>
                  <Select 
                    value={formData.period_months} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, period_months: value }))}
                  >
                    <SelectTrigger className="bg-[#121212] border-white/10 text-white" data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      {periodOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-white/10">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleSimulate}
                  disabled={loading}
                  className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold py-6"
                  data-testid="simulate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Calculando...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-5 h-5 mr-2" />
                      Simular Investimento
                    </>
                  )}
                </Button>
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {result ? (
                <>
                  {/* Main Result Card */}
                  <div className="bg-gradient-to-br from-[#00FF94]/10 to-[#0A0A0A] border border-[#00FF94]/30 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">{result.investment_type}</h3>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Total Investido</span>
                        <span className="text-xl font-bold text-white font-mono">
                          {formatCurrency(result.total_invested)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Rendimento Bruto</span>
                        <span className="text-xl font-bold text-[#00FF94] font-mono">
                          +{formatCurrency(result.gross_earnings)}
                        </span>
                      </div>

                      {result.ir_rate > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500">IR ({(result.ir_rate * 100).toFixed(1)}%)</span>
                          <span className="text-red-400 font-mono">
                            -{formatCurrency(result.ir_amount)}
                          </span>
                        </div>
                      )}

                      <div className="pt-4 border-t border-white/10">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-semibold">Valor Final Liquido</span>
                          <span className="text-2xl font-black text-[#00FF94] font-mono">
                            {formatCurrency(result.net_value)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-gray-500 text-sm">Ganho Liquido</span>
                          <span className="text-[#00FF94] font-mono">
                            +{formatCurrency(result.net_earnings)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info Card */}
                  <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-400">
                          Taxa anual considerada: <span className="text-white font-mono">{(result.annual_rate * 100).toFixed(2)}%</span>
                        </p>
                        <p className="text-xs text-gray-600 mt-2">
                          {result.disclaimer}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <Button
                    variant="outline"
                    className="w-full border-white/10 text-white hover:bg-white/5 rounded-full"
                    onClick={() => navigate('/investments')}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Ver detalhes deste investimento
                  </Button>
                </>
              ) : (
                <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-12 text-center">
                  <Calculator className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Configure sua simulação
                  </h3>
                  <p className="text-gray-500">
                    Preencha os campos ao lado e clique em simular para ver os resultados
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Disclaimer */}
          <motion.p 
            className="text-center text-sm text-gray-600 mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            As simulações são baseadas em taxas atuais e não garantem rentabilidades futuras.
          </motion.p>
        </div>
      </main>

      {/* Quick Navigation */}
      <QuickNav />
    </div>
  );
};

export default SimulatorPage;
